import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link } from "react-router-dom";
import { FiChevronDown, FiLock } from "react-icons/fi";
import { formatMoney, getOrderTotals } from "../utils/commerce";

const initialForm = {
  email: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
};

const supportedCountries = [
  "Hong Kong", "Australia", "Austria", "Belgium", "Canada", "Denmark",
  "Finland", "France", "Germany", "Ireland", "Italy", "Japan",
  "Luxembourg", "Malaysia", "Netherlands", "New Zealand", "Norway",
  "Portugal", "Singapore", "South Korea", "Spain", "Sweden",
  "Switzerland", "Taiwan", "Thailand", "United Kingdom", "United States",
];

function CheckoutField({
  label,
  name,
  form,
  errors,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
}) {
  return (
    <div className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <label htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder || label}
        autoComplete={autoComplete}
        value={form[name]}
        onChange={onChange}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
      />
      {errors[name] && (
        <span id={`${name}-error`} role="alert">
          {errors[name]}
        </span>
      )}
    </div>
  );
}

function CheckoutOrderSummary({
  lines,
  subtotal,
  shipping,
  total,
}) {
  return (
    <div className="checkout-summary__inner">
      <div className="checkout-products">
        {lines.map(({ product, quantity, size, color, lineTotal }) => (
          <div
            className="checkout-product"
            key={`${product.id}-${size}-${color}`}
          >
            <div className="checkout-product__image">
              <img src={product.image} alt="" />
              <span>{quantity}</span>
            </div>
            <div>
              <strong>{product.name}</strong>
              <span>
                {color} / {size}
              </span>
            </div>
            <b>{formatMoney(lineTotal)}</b>
          </div>
        ))}
      </div>
      <dl className="checkout-totals">
        <div>
          <dt>Subtotal</dt>
          <dd>{formatMoney(subtotal)}</dd>
        </div>
        <div>
          <dt>Shipping</dt>
          <dd>{shipping === 0 ? "Free" : formatMoney(shipping)}</dd>
        </div>
        <div className="checkout-total">
          <dt>
            Total <small>USD</small>
          </dt>
          <dd>{formatMoney(total)}</dd>
        </div>
      </dl>
    </div>
  );
}

function CheckOut() {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { lines, subtotal, shipping, total } = useMemo(
    () => getOrderTotals(cartItems, productData),
    [cartItems]
  );

  const update = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    setForm((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      next.email = "Enter a valid email address.";
    ["firstName", "lastName", "address", "city", "postalCode"].forEach(
      (field) => {
        if (!form[field].trim()) next[field] = "This field is required.";
      }
    );
    return next;
  };

  const placeOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      window.setTimeout(
        () =>
          document.querySelector(".checkout-field.has-error input")?.focus(),
        0
      );
      return;
    }

    setSubmitting(true);
    const randomPart = window.crypto?.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(2))).join("")
      : `${Date.now()}${Math.floor(Math.random() * 1000000)}`;
    const orderNo = `NM${Date.now()}${randomPart}`.slice(0, 30);
    const order = {
      number: orderNo,
      createdAt: new Date().toISOString(),
      customer: `${form.firstName} ${form.lastName}`,
      email: form.email,
      total,
      items: lines.reduce((sum, line) => sum + line.quantity, 0),
    };
    window.localStorage.setItem("nartvmora-last-order", JSON.stringify(order));

    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo,
      amount: total.toFixed(2),
      currency: "USD",
      email: form.email,
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: lines[0].product.name,
    });
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (lines.length === 0) {
    return (
      <main className="checkout-empty">
        <Link className="checkout-wordmark" to="/">
          <img src="/brand-wordmark.svg" alt="nartvmora" />
        </Link>
        <h1>Your bag is empty</h1>
        <p>Add a style before starting checkout.</p>
        <Link className="button button--dark" to="/shop">
          Return to shop
        </Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-header">
        <Link className="checkout-wordmark" to="/">
          <img src="/brand-wordmark.svg" alt="nartvmora" />
        </Link>
        <div>
          <FiLock /> Secure checkout
        </div>
      </header>
      <details className="mobile-order-summary">
        <summary>
          Show order summary <FiChevronDown />
          <strong>{formatMoney(total)}</strong>
        </summary>
        <CheckoutOrderSummary
          lines={lines}
          subtotal={subtotal}
          shipping={shipping}
          total={total}
        />
      </details>
      <div className="checkout-layout">
        <section className="checkout-form-column">
          <form id="checkout-form" onSubmit={placeOrder} noValidate>
            <div className="checkout-section checkout-contact">
              <CheckoutField
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                form={form}
                errors={errors}
                onChange={update}
              />
            </div>

            <div className="checkout-section">
              <h2>Delivery</h2>
              <div className="checkout-field">
                <label htmlFor="country">Country / Region</label>
                <select
                  id="country"
                  name="country"
                  value={form.country}
                  onChange={update}
                >
                  {supportedCountries.map((country) => <option key={country}>{country}</option>)}
                </select>
              </div>
              <div className="checkout-row">
                <CheckoutField
                  label="First name"
                  name="firstName"
                  autoComplete="given-name"
                  form={form}
                  errors={errors}
                  onChange={update}
                />
                <CheckoutField
                  label="Last name"
                  name="lastName"
                  autoComplete="family-name"
                  form={form}
                  errors={errors}
                  onChange={update}
                />
              </div>
              <CheckoutField
                label="Company (optional)"
                name="company"
                autoComplete="organization"
                form={form}
                errors={errors}
                onChange={update}
              />
              <CheckoutField
                label="Address"
                name="address"
                autoComplete="street-address"
                form={form}
                errors={errors}
                onChange={update}
              />
              <CheckoutField
                label="Apartment, suite, etc. (optional)"
                name="apartment"
                form={form}
                errors={errors}
                onChange={update}
              />
              <div className="checkout-row checkout-row--three">
                <CheckoutField
                  label="Suburb / City"
                  name="city"
                  autoComplete="address-level2"
                  form={form}
                  errors={errors}
                  onChange={update}
                />
                <CheckoutField label="State / Region" name="state" autoComplete="address-level1" form={form} errors={errors} onChange={update} />
                <CheckoutField
                  label="Postcode"
                  name="postalCode"
                  autoComplete="postal-code"
                  form={form}
                  errors={errors}
                  onChange={update}
                />
              </div>
            </div>

            <div className="checkout-section">
              <h2>Shipping method</h2>
              <label className="shipping-method">
                <span>
                  <input type="radio" checked readOnly /> Standard shipping
                  <small>Estimated 3–7 business days</small>
                </span>
                <strong>
                  {shipping === 0 ? "FREE" : formatMoney(shipping)}
                </strong>
              </label>
            </div>

            <button
              className="checkout-pay"
              type="submit"
              disabled={submitting}
            >
              {submitting
                ? "Redirecting to payment…"
                : `Pay now — ${formatMoney(total)}`}
            </button>
          </form>
          <footer className="checkout-policies">
            <Link to="/returns">Refund policy</Link>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/cookies">Cookie policy</Link>
            <Link to="/terms">Terms of service</Link>
          </footer>
        </section>
        <aside className="checkout-summary">
          <CheckoutOrderSummary
            lines={lines}
            subtotal={subtotal}
            shipping={shipping}
            total={total}
          />
        </aside>
      </div>
    </main>
  );
}

export default CheckOut;
